import {BehaviorSubject, catchError, filter, lastValueFrom, map, mergeMap, Observable, of, switchMap, tap} from 'rxjs';
import {ServiceAppModels} from '../models/serviceapp-models';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {HelperService} from './helper.service';
import {UserAuthenticationService} from './user-authentication.service';
import {SwissAppConfigModels} from '../models/swissappconfig-models';
import {LocalStorageService} from '../local-storage.service';
import {SWISS_SERVICE_APP_DB_STORE_NAME, SWISS_SERVICE_APP_DOCUMENT_STORE_NAME} from '../models/local-storage-names';
import {inject, Injectable} from '@angular/core';
import WebServiceConfig = SwissAppConfigModels.WebServiceConfig;
import SA_Document = ServiceAppModels.SA_Document;

export interface DocumentSynchronisationTaskInfo {
    totalDocuments: number;
    totalDocumentToDownload: number;
    totalDocsAlreadyInStorage: number;
    failedDocuments$: Observable<number>;
    completedDocuments$: Observable<number>;
    totalDownloadedDocuments$: Observable<number>;
}

@Injectable()
export class ServiceAppDocumentTaskFactory {
    private http = inject(HttpClient);
    private helperService = inject(HelperService);
    private userAuthService = inject(UserAuthenticationService);
    private localStorageService = inject(LocalStorageService);

    constructor() {
    }

    public async createDocumentSyncTask(webServiceConfig: WebServiceConfig): Promise<ServiceAppSyncTask> {
        const task = new ServiceAppSyncTask(webServiceConfig, this.http, this.helperService, this.userAuthService, this.localStorageService);
        await task.init();
        return task;
    }
}

export class ServiceAppSyncTask {
    private totalDocumentToDownloadOrCheckCount = 0;
    private documentsInStorage: SA_Document[] = [];
    private docsToDownLoad: SA_Document[] = [];
    private totalDocumentToDownload = 0;
    private syncDate: any;
    private totalDocsAlreadyInStorage = 0;

    private completedDocuments$ = new BehaviorSubject(0);
    private failedDocumentCount$ = new BehaviorSubject(0);
    private totalDownloadedDocuments$ = new BehaviorSubject(0);

    private completedDocuments = 0;
    private failedDocuments = 0;
    private initiated = false;

    constructor(private webServiceConfig: WebServiceConfig, private http: HttpClient, private helperService: HelperService, private userAuthService: UserAuthenticationService, private localStorageService: LocalStorageService) {
    }

    public async init(): Promise<boolean> {
        const documentsToStore = await this.localStorageService.get('objektDokumente', SWISS_SERVICE_APP_DB_STORE_NAME, false, []);
        this.totalDocumentToDownloadOrCheckCount = documentsToStore.length;
        this.documentsInStorage = documentsToStore;
        this.syncDate = new Date(await this.localStorageService.get('last_SA_sync', 'SwissWebConfigDB', true, ''));

        this.docsToDownLoad = (await Promise.all(documentsToStore.map(async (document: SA_Document) => (
            {
                value: document,
                include: (!(await this.documentInLocalStorage(document.documentID)) || (document.fileState === ServiceAppModels.SA_FileStates.FILE && document.fileModifiedDate && new Date(document.fileModifiedDate) > this.syncDate))
            })
        )))
            .filter(v => v.include)
            .map(data => data.value);

        this.totalDocumentToDownload = this.docsToDownLoad.length;
        this.totalDocsAlreadyInStorage = this.totalDocumentToDownloadOrCheckCount - this.totalDocumentToDownload;
        this.initiated = true;
        return true;
    }

    public getDownloadInformation(): DocumentSynchronisationTaskInfo {
        return {
            totalDocuments: this.totalDocumentToDownloadOrCheckCount,
            totalDocumentToDownload: this.totalDocumentToDownload,
            totalDocsAlreadyInStorage: this.totalDocsAlreadyInStorage,
            failedDocuments$: this.failedDocumentCount$.pipe(),
            completedDocuments$: this.completedDocuments$.pipe(),
            totalDownloadedDocuments$: this.totalDownloadedDocuments$.pipe(),
        };
    }

    public async downloadDocuments(): Promise<boolean> {
        if (!this.initiated) {
            throw new Error('need to call init first');
        }
        if (!this.documentsInStorage) {
            throw new Error('no documents in storage');
        }

        if (this.totalDocumentToDownload === 0) {
            return true;
        }

        const observable = of(...this.docsToDownLoad).pipe(
            mergeMap(
                d => this.downloadDokument(d).pipe(
                    filter(r => !!r),
                    map(r => ({
                        documentID: d.documentID,
                        description: d.description,
                        createdBy: d.createdBy,
                        createdDate: d.createdDate,
                        objectID: d.objectID,
                        fileName: d.fileName,
                        binary: r,
                        uploaded: true,
                    } as ServiceAppModels.DokumentDBFile)),
                    switchMap(f => this.addDokumentInDB(f)),
                    tap(() => this.completedDocuments++),
                    tap(() => this.completedDocuments$.next(this.completedDocuments)),
                    tap(() => this.totalDownloadedDocuments$.next(this.completedDocuments + this.failedDocuments)),
                    catchError(err => of(err).pipe(
                        tap(() => this.failedDocuments++),
                        tap(() => this.failedDocumentCount$.next(this.failedDocuments)),
                        tap(() => this.totalDownloadedDocuments$.next(this.completedDocuments + this.failedDocuments)),
                    ))
                ),
                4) // limit concurrent downloads to 4 in order to not overload the client memory or the network (especially when downloading big files)
        );

        await lastValueFrom(observable);
        return true;
    }

    private downloadDokument(document: ServiceAppModels.SA_Document): Observable<any> {
        const headers = new HttpHeaders({
            Authorization: this.helperService.getSwissAuthorization(this.userAuthService.currentUserInfo),
        });

        const url = this.webServiceConfig.dokuUrl;
        return this.http.get<any>(url + '/DownloadDatei?id=' + document.documentID + '&loginname=' + this.userAuthService.getCurrentAuthenticatedUser()?.loginname, {headers, responseType: 'blob' as 'json'});
    }

    private async documentInLocalStorage(docId: string): Promise<boolean> {
        const doc = await this.localStorageService.get(docId, SWISS_SERVICE_APP_DOCUMENT_STORE_NAME);
        return !!doc;
    }

    private async addDokumentInDB(dokumentFile: ServiceAppModels.DokumentDBFile): Promise<void> {
        await this.localStorageService.add(dokumentFile.documentID, dokumentFile, SWISS_SERVICE_APP_DOCUMENT_STORE_NAME);
    }
}
